import {
  alpha,
  Box,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { Ban, ChevronDown, ChevronRight, Eye, RotateCcw } from 'lucide-react'
import type { TFunction } from 'i18next'
import { Fragment, useState } from 'react'

import type { BackupPlanRunLogJob } from '../../../components/BackupPlanRunsPanel'
import { PlanRunScriptsSection } from '../../../components/PlanRunScripts'
import RetryJobDialog from '../../../components/RetryJobDialog'
import {
  getBackupPlanRunRetryDisabledReason,
  shouldShowBackupPlanRunRetryAction,
} from '../../../components/backupPlanRunRetry'
import { formatDateTimeFull, formatRelativeTime, formatTimeRange } from '../../../utils/dateUtils'
import type { BackupPlanRun } from '../../../types'
import { formatRunStatus, isActiveRun } from '../runStatus'
import { findFirstLogJobForRun } from './findFirstLogJobForRun'
import { RunStatusLeadIcon } from './RunStatusLeadIcon'
import { runStatusIconColor } from './runStatusIconColor'

interface PlanRunsHistoryTableProps {
  runs: BackupPlanRun[]
  cancelling: number | null
  onViewLogs: (job: BackupPlanRunLogJob) => void
  onCancel: (runId: number) => void
  onRetry?: (runId: number) => void
  retryingRunId?: number | null
  canRetryRun?: (run: BackupPlanRun) => boolean
  hasActiveRunForPlan?: (run: BackupPlanRun) => boolean
  t: TFunction
}

export function PlanRunsHistoryTable({
  runs,
  cancelling,
  onViewLogs,
  onCancel,
  onRetry,
  retryingRunId = null,
  canRetryRun = () => false,
  hasActiveRunForPlan = () => false,
  t,
}: PlanRunsHistoryTableProps) {
  const [retryRun, setRetryRun] = useState<BackupPlanRun | null>(null)
  const [expandedRuns, setExpandedRuns] = useState<Set<number>>(new Set())
  const toggleExpanded = (runId: number) =>
    setExpandedRuns((prev) => {
      const next = new Set(prev)
      if (next.has(runId)) next.delete(runId)
      else next.add(runId)
      return next
    })
  const formatStatusLabel = (status?: string) =>
    status
      ? t(`backupPlans.statuses.${status}`, { defaultValue: formatRunStatus(status) })
      : t('backupPlans.statuses.unknown')

  if (runs.length === 0) {
    return (
      <Box sx={{ px: 3, py: 2 }}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.disabled',
          }}
        >
          {t('backupPlans.runsTable.empty', { defaultValue: 'No past runs yet.' })}
        </Typography>
      </Box>
    )
  }

  const handleConfirmRetryRun = () => {
    if (!retryRun) return

    const runId = retryRun.id
    setRetryRun(null)
    onRetry?.(runId)
  }

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 64, fontSize: '0.7rem' }}>
                {t('backupPlans.runsTable.columns.run', { defaultValue: 'Run' })}
              </TableCell>
              <TableCell sx={{ fontSize: '0.7rem' }}>
                {t('backupPlans.runsTable.columns.started', { defaultValue: 'Started' })}
              </TableCell>
              <TableCell sx={{ fontSize: '0.7rem' }}>
                {t('backupPlans.runsTable.columns.status', { defaultValue: 'Status' })}
              </TableCell>
              <TableCell sx={{ fontSize: '0.7rem' }}>
                {t('backupPlans.runsTable.columns.duration', { defaultValue: 'Duration' })}
              </TableCell>
              <TableCell align="right" sx={{ width: 112, fontSize: '0.7rem' }}>
                {t('backupPlans.runsTable.columns.actions', { defaultValue: 'Actions' })}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map((run) => {
              const startedAt = run.started_at || run.created_at
              const logJob = findFirstLogJobForRun(run)
              const active = isActiveRun(run.status)
              const scriptCount = run.script_executions?.length ?? 0
              const expanded = expandedRuns.has(run.id)
              const retryDisabledReason = getBackupPlanRunRetryDisabledReason(run, t, {
                canRetry: canRetryRun(run),
                hasActiveRunForPlan: hasActiveRunForPlan(run),
              })
              const retryTooltip =
                retryingRunId === run.id
                  ? t('backupPlans.runsPanel.retryTooltips.retrying')
                  : retryDisabledReason || t('backupPlans.runsPanel.retryTooltips.ready')

              return (
                <Fragment key={run.id}>
                  <TableRow
                    hover
                    sx={expanded ? { '& > td': { borderBottom: 'unset' } } : undefined}
                  >
                    <TableCell
                      sx={{
                        fontFamily: '"JetBrains Mono","Fira Code",ui-monospace,monospace',
                        fontSize: '0.78rem',
                        color: 'text.secondary',
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={0.25}
                        sx={{
                          alignItems: 'center',
                        }}
                      >
                        {scriptCount > 0 ? (
                          <IconButton
                            size="small"
                            onClick={() => toggleExpanded(run.id)}
                            aria-expanded={expanded}
                            aria-label={
                              expanded
                                ? t('backupPlans.runsTable.hideScripts', {
                                    defaultValue: 'Hide scripts',
                                  })
                                : t('backupPlans.runsTable.showScripts', {
                                    defaultValue: 'Show scripts',
                                  })
                            }
                            sx={{ width: 22, height: 22, color: 'text.secondary' }}
                          >
                            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </IconButton>
                        ) : (
                          <Box sx={{ width: 22, flexShrink: 0 }} />
                        )}
                        <span>#{run.id}</span>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {startedAt ? (
                        <Tooltip title={formatDateTimeFull(startedAt)} arrow>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                              cursor: 'help',
                              display: 'inline-block',
                            }}
                          >
                            {formatRelativeTime(startedAt)}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.disabled',
                          }}
                        >
                          —
                        </Typography>
                      )}
                      {/* Who started it, under when: the scheduler or a click. */}
                      <Typography
                        variant="caption"
                        data-testid="plan-run-trigger"
                        sx={{ display: 'block', color: 'text.secondary' }}
                      >
                        {t(`activity.planRun.trigger.${run.trigger}`, {
                          defaultValue: run.trigger,
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            color: runStatusIconColor(run.status),
                            lineHeight: 0,
                          }}
                        >
                          <RunStatusLeadIcon status={run.status} />
                        </Box>
                        <Typography variant="body2">{formatStatusLabel(run.status)}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          fontFamily: '"JetBrains Mono","Fira Code",ui-monospace,monospace',
                          fontSize: '0.78rem',
                        }}
                      >
                        {formatTimeRange(run.started_at, run.completed_at, run.status)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={0.25}
                        sx={{
                          justifyContent: 'flex-end',
                        }}
                      >
                        {logJob && (
                          <Tooltip
                            title={t('backupPlans.runsDialog.viewLogs', {
                              defaultValue: 'View logs',
                            })}
                            arrow
                          >
                            <IconButton
                              size="small"
                              onClick={() => onViewLogs(logJob)}
                              aria-label={t('backupPlans.runsDialog.viewLogs', {
                                defaultValue: 'View logs',
                              })}
                              sx={{
                                width: 28,
                                height: 28,
                                color: 'info.main',
                                '&:hover': {
                                  bgcolor: (theme) => alpha(theme.palette.info.main, 0.12),
                                },
                              }}
                            >
                              <Eye size={15} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onRetry && shouldShowBackupPlanRunRetryAction(run) && (
                          <Tooltip title={retryTooltip} arrow>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (retryDisabledReason) return
                                  setRetryRun(run)
                                }}
                                disabled={retryingRunId === run.id || Boolean(retryDisabledReason)}
                                aria-label={retryTooltip}
                                sx={{
                                  width: 28,
                                  height: 28,
                                  color: 'info.main',
                                  '&:hover': {
                                    bgcolor: (theme) => alpha(theme.palette.info.main, 0.12),
                                  },
                                  '&.Mui-disabled': { opacity: 0.35 },
                                }}
                              >
                                <RotateCcw size={15} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {active && (
                          <Tooltip
                            title={t('backupPlans.runsPanel.cancelRun', {
                              defaultValue: 'Cancel run',
                            })}
                            arrow
                          >
                            <IconButton
                              size="small"
                              onClick={() => onCancel(run.id)}
                              disabled={cancelling === run.id}
                              aria-label={t('backupPlans.runsPanel.cancelRun', {
                                defaultValue: 'Cancel run',
                              })}
                              sx={{
                                width: 28,
                                height: 28,
                                color: 'warning.main',
                                '&:hover': {
                                  bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
                                },
                              }}
                            >
                              <Ban size={15} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                  {scriptCount > 0 && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 0, border: 0 }}>
                        <Collapse in={expanded} unmountOnExit>
                          <Box sx={{ py: 1.5, px: 1 }}>
                            <PlanRunScriptsSection run={run} onViewLogs={onViewLogs} />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <RetryJobDialog
        open={Boolean(retryRun)}
        title={retryRun ? t('backupPlans.runsPanel.retryConfirm', { id: retryRun.id }) : ''}
        confirmLabel={t('backupPlans.runsPanel.retryRun')}
        onClose={() => setRetryRun(null)}
        onConfirm={handleConfirmRetryRun}
      />
    </>
  )
}
